import React, { useEffect, useState } from 'react';
import styles from './CreateStudyConfiguration.module.css';
import { useParams, useLocation } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext.tsx';
import Loader from '../loader/Loader.tsx';
import FieldLoader from '../fieldloader/FieldLoader.tsx';
import Modal from '../modal/Modal.tsx';
import PageHeader from '../pageheader/Pageheader.tsx';
import Dropdown from '../dropdown/Dropdown.tsx';
import SubmitLoader from '../submitloader/SubmitLoader.tsx';
import { useToast } from '../toast/ToastProvider.tsx';
import { 
  getStudyByStudyId, 
  getDefineDropdownValues, 
  getCDISCStandardDropdownValues,
  getCDISCStdVersionDropdownValues,
  getDropdownValues,
  getCTNameDropdownValues,
  getCTNameVersionDropdownValues,
  createStudyGroup,
  getStudygroupDetails,
  createStudy,
  updateStudy
} from '../../api/api.js';
import PageLoader from '../pageloader/PageLoader.tsx';
import ErrorPage from '../errorpage/ErrorPage.tsx';
import Toast from '../toast/Toast.tsx';
import { useNavigate } from 'react-router-dom';
interface StudyFormData {
  selectStudy: DropdownOption | null;
  studyName: string;
  protocolId: string;
  studyDescription: string;
  defineStandard: DropdownOption | null;
  cdiscStandard: DropdownOption | null;
  standardVersion: DropdownOption | null;
  medDRA: DropdownOption | null;
  whoDD: DropdownOption | null;
  snomed: DropdownOption | null;
  loinc: DropdownOption | null;
  unii: DropdownOption | null;
  ndfRtMedRt: DropdownOption | null;
  ctName: DropdownOption | null;
  ctVersion: DropdownOption | null;
  aCRF: File | null;
  supplementDocs: File[];
}

interface DropdownOption {
  id: number;
  referenceKey?: string;
  referenceValue?: string;
  versionCode?: string;
  versionDisplay?: string;
  name?: string;
  version?: string;
  packageName?: string;
  packageType?: string;
  label?: string;
}

interface StudyGroup {
  id: number;
  groupName: string;
  description: string;
  active: boolean;
}

const CreateStudyConfiguration: React.FC = () => {
  const { user } = useUser();
  const [study, setStudy] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'select' | 'create'>('select');
  const showToast = useToast();

  // Dropdown options state
  const [defineOptions, setDefineOptions] = useState<DropdownOption[]>([]);
  const [cdiscOptions, setCdiscOptions] = useState<DropdownOption[]>([]);
  const [standardVersionOptions, setStandardVersionOptions] = useState<DropdownOption[]>([]);
  const [meddraOptions, setMeddraOptions] = useState<DropdownOption[]>([]);
  const [whoddOptions, setWhoddOptions] = useState<DropdownOption[]>([]);
  const [snomedOptions, setSnomedOptions] = useState<DropdownOption[]>([]);
  const [loincOptions, setLoincOptions] = useState<DropdownOption[]>([]);
  const [uniiOptions, setUniiOptions] = useState<DropdownOption[]>([]);
  const [ndfRtMedRtOptions, setNdfRtMedRtOptions] = useState<DropdownOption[]>([]);
  const [ctNameOptions, setCtNameOptions] = useState<DropdownOption[]>([]);
  const [ctVersionOptions, setCtVersionOptions] = useState<any[]>([]);

  // Validation states
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [hasFormChanged, setHasFormChanged] = useState(false);
  const [initialFormData, setInitialFormData] = useState<StudyFormData | null>(null);

  // Loading states for dropdowns
  const [isLoadingStandardVersions, setIsLoadingStandardVersions] = useState(false);
  const [isLoadingCTVersions, setIsLoadingCTVersions] = useState(false);

  // File deletion states
  const [fileToDelete, setFileToDelete] = useState<{
    type: 'aCRF' | 'supplementDocs';
    file: File | null;
    index?: number;
  } | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Form data state
  const [formData, setFormData] = useState<StudyFormData>({
    selectStudy: null,
    studyName: '',
    protocolId: '',
    studyDescription: '',
    defineStandard: null,
    cdiscStandard: null,
    standardVersion: null,
    medDRA: null,
    whoDD: null,
    snomed: null,
    loinc: null,
    unii: null,
    ndfRtMedRt: null,
    ctName: null,
    ctVersion: null,
    aCRF: null,
    supplementDocs: [],
  });

  // Study group states
  const [studyGroups, setStudyGroups] = useState<StudyGroup[]>([]);
  const [groupFormData, setGroupFormData] = useState({
    groupName: '',
    description: ''
  });
  const [groupFormErrors, setGroupFormErrors] = useState({
    groupName: '',
    description: ''
  });
  const [isSubmittingGroup, setIsSubmittingGroup] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
 const navigate = useNavigate();
  // Fetch study groups on mount
  useEffect(() => {
    fetchStudyGroups();
  }, []);

  const fetchStudyGroups = async () => {
    try {
      const { data } = await getStudygroupDetails();
      setStudyGroups(data.studyGroups || []);
      console.log('Study groups loaded:', data.studyGroups);
    } catch (err) {
      console.error('Error fetching study groups:', err);
    }
  };

  // Track initial form state
  useEffect(() => {
    if (!initialFormData && defineOptions.length > 0) {
      setInitialFormData(formData);
    }
  }, [defineOptions.length, formData, initialFormData]);

  // Detect form changes
  useEffect(() => {
    if (!initialFormData) return;

    const hasChanged =
      formData.studyName !== initialFormData.studyName ||
      formData.protocolId !== initialFormData.protocolId ||
      formData.studyDescription !== initialFormData.studyDescription ||
      formData.selectStudy?.id !== initialFormData.selectStudy?.id ||
      formData.defineStandard?.id !== initialFormData.defineStandard?.id ||
      formData.cdiscStandard?.id !== initialFormData.cdiscStandard?.id ||
      formData.standardVersion?.id !== initialFormData.standardVersion?.id ||
      formData.medDRA?.id !== initialFormData.medDRA?.id ||
      formData.whoDD?.id !== initialFormData.whoDD?.id ||
      formData.snomed?.id !== initialFormData.snomed?.id ||
      formData.loinc?.id !== initialFormData.loinc?.id ||
      formData.unii?.id !== initialFormData.unii?.id ||
      formData.ndfRtMedRt?.id !== initialFormData.ndfRtMedRt?.id ||
      formData.ctName?.id !== initialFormData.ctName?.id ||
      formData.ctVersion?.id !== initialFormData.ctVersion?.id ||
      formData.aCRF !== initialFormData.aCRF ||
      formData.supplementDocs.length !== initialFormData.supplementDocs.length;

    setHasFormChanged(hasChanged);
  }, [formData, initialFormData]);

  // Validation function
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.selectStudy) {
      errors.selectStudy = 'Study group is required';
    }
    if (!formData.studyName?.trim()) {
      errors.studyName = 'Study name is required';
    }
    if (!formData.protocolId?.trim()) {
      errors.protocolId = 'Protocol ID is required';
    }
    if (!formData.defineStandard) {
      errors.defineStandard = 'Define standard is required';
    }
    if (!formData.cdiscStandard) {
      errors.cdiscStandard = 'CDISC standard is required';
    }
    if (!formData.standardVersion) {
      errors.standardVersion = 'Standard version is required';
    }
    if (!formData.medDRA) {
      errors.medDRA = 'MedDRA is required';
    }
    if (!formData.whoDD) {
      errors.whoDD = 'WHODD is required';
    }
    if (!formData.snomed) {
      errors.snomed = 'SNOMED is required';
    }
    if (!formData.loinc) {
      errors.loinc = 'LOINC version is required';
    }
    if (!formData.unii) {
      errors.unii = 'UNII version is required';
    }
    if (!formData.ndfRtMedRt) {
      errors.ndfRtMedRt = 'NDF-RT/MED-RT version is required';
    }
    if (!formData.ctName) {
      errors.ctName = 'CT name is required';
    }
    if (!formData.ctVersion) {
      errors.ctVersion = 'CT version is required';
    }

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      showToast.error(firstError);
    }

    return Object.keys(errors).length === 0;
  };

  const clearError = (field: string) => {
    setFormErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  // File handlers
  const handleDeleteFileClick = (
    type: 'aCRF' | 'supplementDocs',
    file: File | null,
    index?: number
  ) => {
    setFileToDelete({ type, file, index });
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!fileToDelete) return;

    if (fileToDelete.type === 'aCRF') {
      setFormData(prev => ({ ...prev, aCRF: null }));
    } else if (fileToDelete.type === 'supplementDocs' && fileToDelete.index !== undefined) {
      setFormData(prev => ({
        ...prev,
        supplementDocs: prev.supplementDocs.filter((_, i) => i !== fileToDelete.index)
      }));
    }

    setIsDeleteModalOpen(false);
    setFileToDelete(null);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setFileToDelete(null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();

    if (['pdf'].includes(ext || '')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="#E53E3E" strokeWidth="2" />
          <path d="M14 2v6h6M9 13h6M9 17h6M9 9h1" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    }
    if (['doc', 'docx'].includes(ext || '')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="#2B6CB0" strokeWidth="2" />
          <path d="M14 2v6h6M9 13h6M9 17h6M9 9h1" stroke="#2B6CB0" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    }
    if (['xls', 'xlsx'].includes(ext || '')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="#38A169" strokeWidth="2" />
          <path d="M14 2v6h6M9 13h6M9 17h6M9 9h1" stroke="#38A169" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    }
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z" stroke="currentColor" strokeWidth="2" />
        <path d="M13 2v7h7" stroke="currentColor" strokeWidth="2" />
      </svg>
    );
  };

  // Modal handlers
  const handleOpenModal = () => {
    setIsModalOpen(true);
    resetGroupForm();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    resetGroupForm();
  };

  const resetGroupForm = () => {
    setGroupFormData({ groupName: '', description: '' });
    setGroupFormErrors({ groupName: '', description: '' });
  };

  const validateGroupForm = () => {
    const errors = { groupName: '', description: '' };
    let isValid = true;

    if (!groupFormData.groupName.trim()) {
      errors.groupName = 'Group name is required';
      isValid = false;
    }
    if (!groupFormData.description.trim()) {
      errors.description = 'Description is required';
      isValid = false;
    }

    setGroupFormErrors(errors);
    return isValid;
  };

  const handleGroupSubmit = async () => {
    if (!validateGroupForm()) return;

    const payload = {
      groupName: groupFormData.groupName,
      description: groupFormData.description,
      active: true
    };

    setIsSubmitting(true);
    setIsSubmittingGroup(true);

    try {
      await createStudyGroup(payload);
      await fetchStudyGroups();

      setTimeout(() => {
        setIsSubmitting(false);
        setIsSubmittingGroup(false);
        handleCloseModal();
        showToast.success('Study group created successfully');
      }, 500);

      console.log('Study group created successfully');
    } catch (err : any) {
      console.error('Error creating study group:', err);
      setIsSubmitting(false);
      setIsSubmittingGroup(false);
      setGroupFormErrors(prev => ({
        ...prev,
        groupName: err.response?.data?.message || 'Failed to create study group'
      }));
    }
  };

  const handleInputChange = (field: keyof StudyFormData, value: string|string[]) => {
    clearError(field);

    if (field === 'selectStudy') {
      const option = studyGroups.find(group => String(group.id) === value);
      setFormData(prev => ({
        ...prev,
        [field]: option ? { id: option.id, value: String(option.id), label: option.groupName } : null
      }));
    } else if (field === 'defineStandard') {
      const option = defineOptions.find(opt => (opt.referenceKey || '') === value);
      setFormData(prev => ({ ...prev, [field]: option || null }));
    } else if (field === 'cdiscStandard') {
      const option = cdiscOptions.find(opt => (opt.referenceKey || '') === value);
      setFormData(prev => ({ ...prev, [field]: option || null }));
    } else if (field === 'standardVersion') {
      const option = standardVersionOptions.find(opt => (opt.versionCode || '') === value);
      setFormData(prev => ({ ...prev, [field]: option || null }));
    } else if (field === 'medDRA') {
      const option = meddraOptions.find(opt => (opt.version || '') === value);
      setFormData(prev => ({ ...prev, [field]: option || null }));
    } else if (field === 'whoDD') {
      const option = whoddOptions.find(opt => (opt.version || '') === value);
      setFormData(prev => ({ ...prev, [field]: option || null }));
    } else if (field === 'snomed') {
      const option = snomedOptions.find(opt => (opt.version || '') === value);
      setFormData(prev => ({ ...prev, [field]: option || null }));
    } else if (field === 'loinc') {
      const option = loincOptions.find(opt => (opt.version || '') === value);
      setFormData(prev => ({ ...prev, [field]: option || null }));
    } else if (field === 'unii') {
      const option = uniiOptions.find(opt => (opt.version || '') === value);
      setFormData(prev => ({ ...prev, [field]: option || null }));
    } else if (field === 'ndfRtMedRt') {
      const option = ndfRtMedRtOptions.find(opt => (opt.version || '') === value);
      setFormData(prev => ({ ...prev, [field]: option || null }));
    } else if (field === 'ctName') {
      const option = ctNameOptions.find(opt => (opt.referenceKey || '') === value);
      setFormData(prev => ({ ...prev, [field]: option || null }));
    } else if (field === 'ctVersion') {
      const option = ctVersionOptions.find(opt => (opt.version || '') === value);
      setFormData(prev => ({ ...prev, [field]: option || null }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }

    if (field === 'cdiscStandard' && value) {
      fetchStandardVersions(value);
    }
    if (field === 'ctName' && value) {
      fetchCTVersions(value);
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  // Fetch all dropdown values on mount
  useEffect(() => {
    setLoading(true);
    const fetchAllDropdowns = async () => {
      try {
        const [defineRes, cdiscRes, allDictRes, ctNameRes] = await Promise.all([
          getDefineDropdownValues(),
          getCDISCStandardDropdownValues(),
          getDropdownValues(),
          getCTNameDropdownValues(),
        ]);

        setDefineOptions(defineRes.data.standardDetails || []);
        setCdiscOptions(cdiscRes.data.standardDetails || []);
        
        const allDictData = allDictRes.data;
        setWhoddOptions(allDictData.WHODD || []);
        setSnomedOptions(allDictData.SNOMED || []);
        setMeddraOptions(allDictData.MEDDRA || []);
        setUniiOptions(allDictData.UNII || []);
        setLoincOptions(allDictData.LOINC || []);
       setNdfRtMedRtOptions(allDictData.NDFRT_MEDRT   || []);
        // setNdfRtMedRtOptions(allDictData["NDFRT_MEDRT "] || []);
        
        setCtNameOptions(ctNameRes.data.standardDetails || []);

        console.log('Dropdown values loaded');
      } catch (err) {
        console.error('Error fetching dropdown values:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllDropdowns();
  }, []);

  const fetchStandardVersions = async (cdiscStandard: string|string[]) => {
    setIsLoadingStandardVersions(true);
    try {
      const response = await getCDISCStdVersionDropdownValues(cdiscStandard);
      const versions =
        response.data.sdtmigVersions ||
        response.data.adamVersions ||
        response.data.sendingVersions ||
        [];
      setStandardVersionOptions(versions);
    } catch (err) {
      console.error('Error fetching standard versions:', err);
      setStandardVersionOptions([]);
    } finally {
      setIsLoadingStandardVersions(false);
    }
  };

  const fetchCTVersions = async (ctName: string|string[]) => {
    setIsLoadingCTVersions(true);
    try {
      const response = await getCTNameVersionDropdownValues(ctName);
      const versions = response.data.versions || [];
      setCtVersionOptions(versions);
    } catch (err) {
      console.error('Error fetching CT versions:', err);
      setCtVersionOptions([]);
    } finally {
      setIsLoadingCTVersions(false);
    }
  };

  const handleFileChange = (field: 'aCRF' | 'supplementDocs', files: FileList | null) => {
    if (field === 'aCRF') {
      setFormData(prev => ({ ...prev, [field]: files?.[0] || null }));
    } else {
      setFormData(prev => ({ ...prev, [field]: files ? Array.from(files) : [] }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const formDataToSend = new FormData();

      const metadata = {
        userId: user?.userId || 1,
        studyName: formData.studyName,
        protocolId: formData.protocolId,
        studyDescription: formData.studyDescription,
        studyGroupId: formData.selectStudy?.id || 1,
        defineStandard: formData.defineStandard?.referenceValue || formData.defineStandard?.referenceKey || '',
        cdiscStandard: formData.cdiscStandard?.referenceValue || formData.cdiscStandard?.referenceKey || '',
        versionStandard: formData.standardVersion?.versionDisplay || formData.standardVersion?.versionCode || '',
        meddraDictionary: formData.medDRA?.version || '',
        whoDictionary: formData.whoDD?.version || '',
        snomedDictionary: formData.snomed?.version || '',
        loincDictionary: formData.loinc?.version || '',
        uniiDictionary: formData.unii?.version || '',
        ndfrtMedrtDictionary: formData.ndfRtMedRt?.version || '',
        ctName: formData.ctName?.referenceValue || formData.ctName?.referenceKey || '',
        ctVersion: formData.ctVersion?.packageName || '',
        createdBy: user?.userName || ''
      };

      const metadataBlob = new Blob([JSON.stringify(metadata)], { type: 'application/json' });
      formDataToSend.append('metadata', metadataBlob);

      if (formData.aCRF) {
        formDataToSend.append('crfDocument', formData.aCRF);
      }

      if (formData.supplementDocs && formData.supplementDocs.length > 0) {
        formData.supplementDocs.forEach((file: File) => {
          formDataToSend.append('supportingDocs', file);
        });
      }

      setIsSubmitting(true);

      const response = await createStudy(formDataToSend);
      console.log('Study created successfully:', response.data);

      if (response.status === 200) {
        showToast.success('Study created successfully');
        setTimeout(() => {
          setIsSubmitting(false);
          handleReset();
        }, 500);

        navigate('/defineui/assigned-studies');
      }
    } catch (error) {
      console.error('Error creating study:', error);
      showToast.error('Error creating study');
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      selectStudy: null,
      studyName: '',
      protocolId: '',
      studyDescription: '',
      defineStandard: null,
      cdiscStandard: null,
      standardVersion: null,
      medDRA: null,
      whoDD: null,
      snomed: null,
      loinc: null,
      unii: null,
      ndfRtMedRt: null,
      ctName: null,
      ctVersion: null,
      aCRF: null,
      supplementDocs: [],
    });
    setStandardVersionOptions([]);
    setCtVersionOptions([]);
    setFormErrors({});
    setHasFormChanged(false);
    setInitialFormData(null);
  };


  const handleContinue = () => {
    // Use window.location.href for full page reload to ensure scroll reset
    window.location.href = '/defineui/xptfileupload';
  };

  const handleCancel = () => {
    handleReset();
    navigate('/defineui/assigned-studies');
  };

  if (loading) {
    return <PageLoader message="Loading study configuration..." />;
  }

  if (error) {
    return <ErrorPage title="Error Loading Study Configuration" message={error} onRetry={handleRefresh} />;
  }

  const userPermissions = {
    canCreateStudy: true,
    canViewDefineFiles: true,
    canUploadDocuments: true,
    canViewDefinePreview: true,
    canAccessIssueManagement: true,
    canViewProgressCharts: true,
  };

  return (
    <>
      <PageHeader
        title="Create Study"
        showRefresh={false}
        showCreateStudy={true}
        showDefineFiles={true}
        showUploadDocuments={true}
        showDefinePreview={true}
        showIssueManagement={true}
        showProgressCharts={true}
        selectedStudyId={0}
        permissions={userPermissions}
        onCreateStudy={() => console.log('Create new study')}
        onDefineFiles={() => console.log('View define files')}
        onUploadDocuments={() => console.log('Upload documents')}
        onDefinePreview={() => console.log('View define preview')}
        onIssueManagement={() => console.log('Issue management')}
        onProgressCharts={() => console.log('Progress charts')}
      />

      <div className={styles.studyConfigContainer}>
        <div className={styles.formContent}>
          {/* Study Details Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Study Details</h2>
            </div>

            <div className={styles.selectStudyRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Select Study <span className={styles.required}>*</span>
                </label>
                <Dropdown
                  options={studyGroups.map(group => ({
                    value: String(group.id),
                    label: group.groupName,
                  }))}
                  value={formData.selectStudy ? String(formData.selectStudy.id) : ''}
                  onChange={value => handleInputChange('selectStudy', value)}
                  placeholder="Select Study Group"
                  disabled={isSubmitting}
                />
                {formErrors.selectStudy && (
                  <span className={styles.errorText}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                      <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    {formErrors.selectStudy}
                  </span>
                )}
              </div>

              <button className={styles.createGroupButtonInline} onClick={handleOpenModal} title="Add Study Group">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Study Name<span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  className={`${styles.input} ${formErrors.studyName ? styles.inputError : ''}`}
                  placeholder="Enter your Text"
                  value={formData.studyName}
                  onChange={e => handleInputChange('studyName', e.target.value)}
                  disabled={isSubmitting}
                />
                {formErrors.studyName && (
                  <span className={styles.errorText}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                      <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    {formErrors.studyName}
                  </span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Protocol ID<span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  className={`${styles.input} ${formErrors.protocolId ? styles.inputError : ''}`}
                  placeholder="Enter your Text"
                  value={formData.protocolId}
                  onChange={e => handleInputChange('protocolId', e.target.value)}
                  disabled={isSubmitting}
                />
                {formErrors.protocolId && (
                  <span className={styles.errorText}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                      <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    {formErrors.protocolId}
                  </span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Study Description</label>
                <textarea
                  className={styles.textarea}
                  placeholder="Enter your Text"
                  value={formData.studyDescription}
                  onChange={e => handleInputChange('studyDescription', e.target.value)}
                  rows={3}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </section>

          {/* Create Study Group Modal */}
          <Modal isOpen={isModalOpen} onClose={handleCloseModal} title="Create Study Group">
            <div className={styles.modalForm}>
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Group Name<span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  className={`${styles.input} ${groupFormErrors.groupName ? styles.error : ''}`}
                  placeholder="Enter group name"
                  value={groupFormData.groupName}
                  onChange={e => setGroupFormData(prev => ({ ...prev, groupName: e.target.value }))}
                />
                {groupFormErrors.groupName && (
                  <span className={styles.errorText}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                      <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    {groupFormErrors.groupName}
                  </span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Description<span className={styles.required}>*</span>
                </label>
                <textarea
                  className={`${styles.textarea} ${groupFormErrors.description ? styles.error : ''}`}
                  placeholder="Enter group description"
                  value={groupFormData.description}
                  onChange={e => setGroupFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={4}
                />
                {groupFormErrors.description && (
                  <span className={styles.errorText}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                      <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    {groupFormErrors.description}
                  </span>
                )}
              </div>

              <div className={styles.modalFooter}>
                <button
                  className={`${styles.submitButton} ${isSubmittingGroup ? styles.loading : ''}`}
                  onClick={handleGroupSubmit}
                  disabled={isSubmittingGroup}
                >
                  Create Group
                </button>
                <button className={styles.cancelButton} onClick={handleCloseModal} disabled={isSubmittingGroup}>
                  Cancel
                </button>
              </div>
            </div>
          </Modal>

          {/* Standard Details Section */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Standard Details</h2>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Select Define Standard<span className={styles.required}>*</span>
                </label>
                <Dropdown
                  options={defineOptions.map(opt => ({
                    value: opt.referenceKey || '',
                    label: opt.referenceValue || ''
                  }))}
                  value={formData.defineStandard?.referenceKey || ''}
                  onChange={value => handleInputChange('defineStandard', value)}
                  placeholder="Select"
                  disabled={isSubmitting}
                />
                {formErrors.defineStandard && (
                  <span className={styles.errorText}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                      <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    {formErrors.defineStandard}
                  </span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  CDISC Standard<span className={styles.required}>*</span>
                </label>
                <Dropdown
                  options={cdiscOptions.map(opt => ({
                    value: opt.referenceKey || '',
                    label: opt.referenceValue || ''
                  }))}
                  value={formData.cdiscStandard?.referenceKey || ''}
                  onChange={value => handleInputChange('cdiscStandard', value)}
                  placeholder="Select"
                  disabled={isSubmitting}
                />
                {formErrors.cdiscStandard && (
                  <span className={styles.errorText}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                      <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    {formErrors.cdiscStandard}
                  </span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Standard Version<span className={styles.required}>*</span>
                  {isLoadingStandardVersions && <FieldLoader style={{ marginLeft: '8px' }} />}
                </label>
                <Dropdown
                  options={standardVersionOptions.map(opt => ({
                    value: opt.versionCode || '',
                    label: opt.versionDisplay || ''
                  }))}
                  value={formData.standardVersion?.versionCode || ''}
                  onChange={value => handleInputChange('standardVersion', value)}
                  placeholder="Select"
                  disabled={
                    !formData.cdiscStandard ||
                    standardVersionOptions.length === 0 ||
                    isLoadingStandardVersions ||
                    isSubmitting
                  }
                />
                {formErrors.standardVersion && (
                  <span className={styles.errorText}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                      <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    {formErrors.standardVersion}
                  </span>
                )}
              </div>
            </div>
          </section>

          {/* Dictionaries Section */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Dictionaries</h2>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  MedDRA<span className={styles.required}>*</span>
                </label>
                <Dropdown
                  options={meddraOptions.map(opt => ({
                    value: opt.version || '',
                    label: opt.version || ''
                  }))}
                  value={formData.medDRA?.version || ''}
                  onChange={value => handleInputChange('medDRA', value)}
                  placeholder="Select"
                  disabled={isSubmitting}
                />
                {formErrors.medDRA && (
                  <span className={styles.errorText}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                      <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    {formErrors.medDRA}
                  </span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  WHODD<span className={styles.required}>*</span>
                </label>
                <Dropdown
                  options={whoddOptions.map(opt => ({
                    value: opt.version || '',
                    label: opt.version || ''
                  }))}
                  value={formData.whoDD?.version || ''}
                  onChange={value => handleInputChange('whoDD', value)}
                  placeholder="Select"
                  disabled={isSubmitting}
                />
                {formErrors.whoDD && (
                  <span className={styles.errorText}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                      <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    {formErrors.whoDD}
                  </span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  SNOMED<span className={styles.required}>*</span>
                </label>
                <Dropdown
                  options={snomedOptions.map(opt => ({
                    value: opt.version || '',
                   label: opt.version || ''
                  }))}
                  value={formData.snomed?.version || ''}
                  onChange={value => handleInputChange('snomed', value)}
                  placeholder="Select"
                  disabled={isSubmitting}
                />
                {formErrors.snomed && (
                  <span className={styles.errorText}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                      <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    {formErrors.snomed}
                  </span>
                )}
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  LOINC Version<span className={styles.required}>*</span>
                </label>
                <Dropdown
                  options={loincOptions.map(opt => ({
                    value: opt.version || '',
                    label: opt.version || ''
                  }))}
                  value={formData.loinc?.version || ''}
                  onChange={value => handleInputChange('loinc', value)}
                  placeholder="Select"
                  disabled={isSubmitting}
                />
                {formErrors.loinc && (
                  <span className={styles.errorText}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                      <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    {formErrors.loinc}
                  </span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  UNII Version<span className={styles.required}>*</span>
                </label>
                <Dropdown
                  options={uniiOptions.map(opt => ({
                    value: opt.version || '',
                   label: opt.version || ''
                  }))}
                  value={formData.unii?.version || ''}
                  onChange={value => handleInputChange('unii', value)}
                  placeholder="Select"
                  disabled={isSubmitting}
                />
                {formErrors.unii && (
                  <span className={styles.errorText}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                      <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    {formErrors.unii}
                  </span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  NDF-RT/MED-RT Version<span className={styles.required}>*</span>
                </label>
                <Dropdown
                  options={ndfRtMedRtOptions.map(opt => ({
                    value: opt.version || '',
                    label: opt.version || ''
                  }))}
                  value={formData.ndfRtMedRt?.version || ''}
                  onChange={value => handleInputChange('ndfRtMedRt', value)}
                  placeholder="Select"
                  disabled={isSubmitting}
                />
                {formErrors.ndfRtMedRt && (
                  <span className={styles.errorText}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                      <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    {formErrors.ndfRtMedRt}
                  </span>
                )}
              </div>
            </div>
          </section>

          {/* Controlled Terminologies Section */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Controlled Terminologies</h2>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  CT Name<span className={styles.required}>*</span>
                </label>
                <Dropdown
                  options={ctNameOptions.map(opt => ({
                    value: opt.referenceKey || '',
                    label: opt.referenceValue?.toUpperCase() || ''
                  }))}
                  value={formData.ctName?.referenceKey || ''}
                  onChange={value => handleInputChange('ctName', value)}
                  placeholder="Select"
                  disabled={isSubmitting}
                />
                {formErrors.ctName && (
                  <span className={styles.errorText}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                      <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    {formErrors.ctName}
                  </span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  CT Version<span className={styles.required}>*</span>
                  {isLoadingCTVersions && <FieldLoader style={{ marginLeft: '8px' }} />}
                </label>
                <Dropdown
                  options={ctVersionOptions.map(opt => ({
                    value: opt.version || '',
                    label: opt.name || ''
                  }))}
                  value={formData.ctVersion?.version || ''}
                  onChange={value => handleInputChange('ctVersion', value)}
                  placeholder="Select"
                  disabled={!formData.ctName || ctVersionOptions.length === 0 || isLoadingCTVersions || isSubmitting}
                />
                {formErrors.ctVersion && (
                  <span className={styles.errorText}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                      <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    {formErrors.ctVersion}
                  </span>
                )}
              </div>
            </div>
          </section>

          {/* Attachments Section */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Attachments</h2>

            <div className={styles.formRow}>
              {/* aCRF Upload */}
              <div className={styles.formGroup}>
                <label className={styles.label}>Attach aCRF</label>

                {!formData.aCRF && (
                  <div className={styles.fileInput}>
                    <input
                      type="file"
                      id="aCRF"
                      className={styles.fileInputHidden}
                      onChange={e => handleFileChange('aCRF', e.target.files)}
                      disabled={isSubmitting}
                    />
                    <label htmlFor="aCRF" className={styles.fileInputLabel}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                        <path d="M13 2v7h7" stroke="currentColor" strokeWidth="2" />
                      </svg>
                      <span>Select File</span>
                    </label>
                  </div>
                )}

                {formData.aCRF && (
                  <div className={styles.fileList}>
                    <div className={styles.fileItem}>
                      <div className={styles.fileIcon}>{getFileIcon(formData.aCRF.name)}</div>
                      <div className={styles.fileInfo}>
                        <div className={styles.fileName}>{formData.aCRF.name}</div>
                        <div className={styles.fileSize}>{formatFileSize(formData.aCRF.size)}</div>
                      </div>
                      <button
                        type="button"
                        className={styles.deleteFileButton}
                        onClick={() => handleDeleteFileClick('aCRF', formData.aCRF)}
                        disabled={isSubmitting}
                        title="Remove file"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M18 6L6 18M6 6l12 12"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Supplement Documents Upload */}
              <div className={styles.formGroup}>
                <label className={styles.label}>Attach Supplement Documents</label>

                <div className={styles.fileInput}>
                  <input
                    type="file"
                    id="supplementDocs"
                    className={styles.fileInputHidden}
                    multiple
                    onChange={e => handleFileChange('supplementDocs', e.target.files)}
                    disabled={isSubmitting}
                  />
                  <label htmlFor="supplementDocs" className={styles.fileInputLabel}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path d="M13 2v7h7" stroke="currentColor" strokeWidth="2" />
                    </svg>
                    <span>
                      {formData.supplementDocs.length > 0
                        ? `Add More Files (${formData.supplementDocs.length} selected)`
                        : 'Select Files'}
                    </span>
                  </label>
                </div>

                {formData.supplementDocs.length > 0 && (
                  <div className={styles.fileList}>
                    {formData.supplementDocs.map((file, index) => (
                      <div key={index} className={styles.fileItem}>
                        <div className={styles.fileIcon}>{getFileIcon(file.name)}</div>
                        <div className={styles.fileInfo}>
                          <div className={styles.fileName}>{file.name}</div>
                          <div className={styles.fileSize}>{formatFileSize(file.size)}</div>
                        </div>
                        <button
                          type="button"
                          className={styles.deleteFileButton}
                          onClick={() => handleDeleteFileClick('supplementDocs', file, index)}
                          disabled={isSubmitting}
                          title="Remove file"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M18 6L6 18M6 6l12 12"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>

        {/* Footer Buttons */}
        <div className={styles.footer}>
          <button
            className={styles.submitButton}
            onClick={handleSubmit}
            disabled={isSubmitting || !hasFormChanged}
            title={!hasFormChanged ? 'Make changes to enable save' : 'Save changes'}
          >
            Save
          </button>
          <button className={styles.resetButton} onClick={handleContinue} disabled={isSubmitting}>
            Continue
          </button>
          <button className={styles.cancelButton} onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </button>
        </div>
      </div>

      <SubmitLoader isVisible={isSubmitting} type="create" message="Creating Study Configuration..." />

      {/* Delete File Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={handleCancelDelete} title="Delete File">
        <div className={styles.deleteModalContent}>
          <div className={styles.deleteWarningIcon}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#E53E3E" strokeWidth="2" />
              <path d="M12 8v4M12 16h.01" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>

          <h3 className={styles.deleteModalTitle}>Are you sure you want to delete this file?</h3>

          <p className={styles.deleteModalMessage}>{fileToDelete?.file?.name}</p>

          <p className={styles.deleteModalWarning}>This action cannot be undone.</p>

          <div className={styles.deleteModalActions}>
            <button className={styles.deleteModalButton} onClick={handleConfirmDelete}>
              Delete
            </button>
            <button className={styles.cancelModalButton} onClick={handleCancelDelete}>
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CreateStudyConfiguration;